import { useState } from 'react'
import { useHeadshot } from '../lib/headshots'
import { initials } from '../lib/format'
import type { Agent } from '../lib/types'

/**
 * The agent's photograph, or their initials where there is no photograph.
 *
 * Initials are also what appears if the signed link has expired or the image
 * fails to load, so the worst case is exactly what the screen looked like
 * before photos existed rather than a broken-image icon.
 *
 * The failure is remembered as *which URL* failed, not as a bare "broken" flag.
 * The detail panel reuses one instance of this component as you click from
 * agent to agent, and a flag would carry one agent's failure onto the next
 * one's face for a frame.
 */
export default function Headshot({
  agent,
  size,
}: {
  agent: Agent
  size: 'list' | 'detail'
}) {
  const url = useHeadshot(agent.rezen_id)
  const [failedUrl, setFailedUrl] = useState<string | null>(null)

  if (!url || failedUrl === url) {
    return <span className={size === 'list' ? 'av ph' : 'bigph'}>{initials(agent)}</span>
  }

  return (
    <img
      className={size === 'list' ? 'av' : 'bigph'}
      src={url}
      // The agent's name sits immediately beside this in both places it is
      // used, so announcing it again would only repeat it.
      alt=""
      // The list renders all 785 rows at once; the detail panel renders one
      // photo that is always on screen.
      loading={size === 'list' ? 'lazy' : 'eager'}
      decoding="async"
      onError={() => setFailedUrl(url)}
    />
  )
}
